import { useTranslation } from "react-i18next";
import { useState } from "react";
import { createTags } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { useTags } from "~/api/swr/useTags";
import { includesSearch, toComparableString } from "~/helpers/string";
import {
  Tags,
  TagsTrigger,
  TagsValue,
  TagsContent,
  TagsInput,
  TagsList,
  TagsEmpty,
  TagsGroup,
  TagsItem,
} from "~/molecules/Tags";

interface Props {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export function TagForm({ selectedTags, onTagsChange }: Props) {
  const { t } = useTranslation();
  const { data: tags, mutate: mutateTags } = useTags();
  const [searchValue, setSearchValue] = useState("");

  const tagOptions =
    tags?.map((tag) => ({
      value: tag.id.toString(),
      label: tag.title,
    })) ?? [];

  const handleCreateTag = async (tagName: string) => {
    const createdTags = await createTags([tagName.trim()]);
    if (createdTags?.[0]) {
      const newOption = {
        value: createdTags[0].id.toString(),
        label: createdTags[0].title,
      };

      onTagsChange([...selectedTags, newOption.value]);
      void mutateTags();

      toast.success(
        t(($) => $.toasts.success.tagCreated, { tagName: tagName.trim() }),
      );

      setSearchValue("");
    }
  };

  const handleSelectTag = (tagValue: string) => {
    if (!selectedTags.includes(tagValue)) {
      onTagsChange([...selectedTags, tagValue]);
    }
    setSearchValue("");
  };

  const handleRemoveTag = (tagValue: string) => {
    onTagsChange(selectedTags.filter((tag) => tag !== tagValue));
  };

  const renderCreateTagItem = () => {
    if (
      searchValue.trim() &&
      !tagOptions.some(
        (tag) =>
          toComparableString(tag.label) === toComparableString(searchValue),
      )
    ) {
      return (
        <TagsItem
          value={searchValue}
          onSelect={() => handleCreateTag(searchValue)}
        >
          <div className="flex items-center gap-2">
            <TagsValue>
              {searchValue} ({t(($) => $.modal.tags.newTag)})
            </TagsValue>
          </div>
        </TagsItem>
      );
    }
  };

  const renderSearchResults = () => {
    const filteredTags = tagOptions.filter(
      (tag) =>
        includesSearch(tag.label, searchValue) &&
        !selectedTags.includes(tag.value),
    );

    if (filteredTags.length === 0) {
      return null;
    }

    return (
      <TagsGroup heading={t(($) => $.modal.tags.existingTags)}>
        {filteredTags.map((tag) => (
          <TagsItem
            key={tag.value}
            value={tag.label}
            keywords={[tag.label]}
            onSelect={() => handleSelectTag(tag.value)}
          >
            <TagsValue>{tag.label}</TagsValue>
          </TagsItem>
        ))}
      </TagsGroup>
    );
  };

  const renderEmptyState = () => {
    if (!searchValue.trim() && tagOptions.length === 0) {
      return (
        <TagsEmpty>{t(($) => $.modal.tags.searchToFindOrCreate)}</TagsEmpty>
      );
    }
  };

  return (
    <div className="space-y-3">
      <Tags selectLabel={t(($) => $.modal.tags.selectTag)}>
        <TagsTrigger>
          {selectedTags.map((tagValue) => {
            const tag = tagOptions.find((t) => t.value === tagValue);
            return (
              <TagsValue
                key={tagValue}
                onRemove={() => handleRemoveTag(tagValue)}
              >
                {tag?.label ?? tagValue}
              </TagsValue>
            );
          })}
        </TagsTrigger>
        <TagsContent>
          <TagsInput
            placeholder={t(($) => $.modal.tags.searchOrCreate)}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <TagsList>
            {renderCreateTagItem()}
            {renderSearchResults()}
            {renderEmptyState()}
          </TagsList>
        </TagsContent>
      </Tags>
    </div>
  );
}
